/**
 * FiltersService — service de filtrage du catalogue.
 *
 * Génère la structure des filtres disponibles (avec compteurs en temps réel)
 * depuis un tableau de produits en mémoire, applique les filtres sélectionnés,
 * et persiste les préférences utilisateur dans AsyncStorage.
 *
 * Conserve les IDs de catégories internes en FR ('Robes', 'Hauts', etc.) pour
 * rester compatible avec les data partners et regex matchers existants.
 * Les labels traduits sont injectés via le hook useI18n côté composant.
 *
 * v1.12.345 — refonte filtres catalogue (Phase 1 : service).
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

const PREF_STORAGE_KEY = '@mytenue/catalog_filter_prefs_v1';

// ─── Catégories disponibles (ordre d'affichage) ─────────────────
// Doit matcher les IDs utilisés dans CatalogScreen.SUBFILTERS et CategoryNormalizer.
export const CATEGORY_IDS = [
  'Robes', 'Hauts', 'Bas', 'Vestes', 'Manteaux',
  'Chaussures', 'Sacs', 'Bijoux', 'Accessoires', 'Maillots',
];

// ─── Couleurs normalisées + swatches hex ─────────────────────────
// On essaie de matcher les couleurs FR/EN depuis le champ product.color
// (texte libre) ou ai_metadata.color/colors.
export const COLOR_PALETTE = [
  { value: 'noir',        hex: '#1A1A1A', match: /\b(noir|black|nero)\b/i },
  { value: 'blanc',       hex: '#FFFFFF', match: /\b(blanc|white|ivoire|ivory|écru|ecru|cream|off.?white)\b/i },
  { value: 'gris',        hex: '#808080', match: /\b(gris|grey|gray|anthracite|charcoal)\b/i },
  { value: 'beige',       hex: '#D7B98E', match: /\b(beige|camel|sable|sand|tan|nude|taupe)\b/i },
  { value: 'marron',      hex: '#6F4E37', match: /\b(marron|brown|chocolat|chocolate|cognac|caramel)\b/i },
  { value: 'marine',      hex: '#1E3A8A', match: /\b(marine|navy|bleu.marine|bleu.nuit|midnight)\b/i },
  { value: 'bleu',        hex: '#3B82F6', match: /\b(bleu|blue|azur|cobalt|sky)\b/i },
  { value: 'rouge',       hex: '#C8102E', match: /\b(rouge|red|bordeaux|burgundy|wine)\b/i },
  { value: 'rose',        hex: '#F8B7C9', match: /\b(rose|pink|fuchsia|magenta|salmon)\b/i },
  { value: 'vert',        hex: '#2E8B57', match: /\b(vert|green|kaki|khaki|olive|emerald|sage)\b/i },
  { value: 'jaune',       hex: '#F4C430', match: /\b(jaune|yellow|moutarde|mustard|ocre|ochre)\b/i },
  { value: 'orange',      hex: '#F97316', match: /\b(orange|corail|coral|peche|peach)\b/i },
  { value: 'violet',      hex: '#7C3AED', match: /\b(violet|purple|mauve|lilas|lilac|prune|plum)\b/i },
  { value: 'doré',        hex: '#D4AF37', match: /\b(dor[eé]|gold|golden|or)\b/i },
  { value: 'argenté',     hex: '#C0C0C0', match: /\b(argent[eé]?|silver)\b/i },
  { value: 'multicolore', hex: 'linear-gradient(135deg,#ff0080,#7928ca,#3B82F6)', match: /\b(multicolore|multicolor|imprim[eé]|print|motif|patterned)\b/i },
];

function detectColor(product) {
  const haystack = [
    product?.color,
    product?.ai_metadata?.color,
    product?.ai_metadata?.primaryColor,
    Array.isArray(product?.ai_metadata?.colors) ? product.ai_metadata.colors.join(' ') : null,
    product?.name,
  ].filter(Boolean).join(' ').toLowerCase();
  if (!haystack) return null;
  for (const c of COLOR_PALETTE) {
    if (c.match.test(haystack)) return c.value;
  }
  return null;
}

// ─── Tranches de prix rapides ───────────────────────────────────
export const PRICE_RANGES = [
  { value: '0-50',    min: 0,    max: 50    },
  { value: '50-100',  min: 50,   max: 100   },
  { value: '100-300', min: 100,  max: 300   },
  { value: '300-500', min: 300,  max: 500   },
  { value: '500+',    min: 500,  max: null  },
];

// ─── Tiers partenaires (basé sur commission Mytenue) ────────────
// TIER 1 : 12% (ann_demeulemeester, suit_negozi 12%)
// TIER 2 : 8-10% (suit_negozi 8/10%, coutr, autres)
// TIER 3 : ≤7% (tous 6.62%, iqueens, cashmere)
export const PARTNER_TIERS = {
  ann_demeulemeester: 'tier_1',
  suit_negozi:        'tier_1', // par défaut, peut varier selon brand
  coutr:              'tier_2',
  iqueens:            'tier_3',
  cashmere:           'tier_3',
  tous:               'tier_3',
};

export const TIER_LABELS = {
  tier_1: {
    fr: 'Premium',   en: 'Premium',
    badge_fr: '💎 Récompense max',   badge_en: '💎 Max reward',
    maxReward: '4,80€',
  },
  tier_2: {
    fr: 'Standard',  en: 'Standard',
    badge_fr: '⭐ Bonne récompense', badge_en: '⭐ Good reward',
    maxReward: '3,00€',
  },
  tier_3: {
    fr: 'Essentiel', en: 'Essentials',
    badge_fr: '',                   badge_en: '',
    maxReward: '2,00€',
  },
};

// v1.12.348 — Partenaires (multi-select dans le panneau)
//   Mappe le partner clé → label affiché. Ordre = priorité d'affichage.
// v1.12.366 — iqueens retiré (5143 produits disabled en DB sur demande user)
// v1.12.861 — DRESTIGE ajouté (Awin 13% luxe homme + femme)
export const PARTNER_OPTIONS = [
  { value: 'drestige',           label: 'DRESTIGE' },
  { value: 'suit_negozi',        label: 'SUIT' },
  { value: 'coutr',              label: 'COUTR' },
  { value: 'ann_demeulemeester', label: 'ANN' },
  { value: 'tamaris',            label: 'TAMARIS' },
  { value: 'desigual',           label: 'DESIGUAL' },
  { value: 'tous',               label: 'TOUS' },
];

// ─── Styles, matières, occasions (depuis ai_metadata) ────────────
export const STYLE_KEYS = [
  'chic', 'casual', 'smart_casual', 'sportswear', 'bohemian',
  'romantic', 'edgy', 'vintage', 'minimalist', 'glamour',
];

export const MATERIAL_KEYS = [
  'coton', 'soie', 'cuir', 'laine', 'cachemire',
  'denim', 'polyester', 'lin', 'velours', 'satin',
];

export const OCCASION_KEYS = [
  'travail', 'brunch', 'soirée', 'sport', 'cérémonie',
  'casual', 'voyage', 'date',
];

// ─── Helpers ────────────────────────────────────────────────────
function getProductPriceEur(product, usdToEurRate = 0.92) {
  const raw = Number(product?.price) || 0;
  if (raw <= 0) return 0;
  const currency = product?.currency || (product?.partner === 'coutr' ? 'USD' : 'EUR');
  return currency === 'USD' ? raw * usdToEurRate : raw;
}

function getProductTier(product) {
  return PARTNER_TIERS[product?.partner] || 'tier_3';
}

function getProductStyles(product) {
  const scores = product?.ai_metadata?.styleScores;
  if (!scores || typeof scores !== 'object') return [];
  return Object.entries(scores)
    .filter(([_, v]) => Number(v) >= 50)
    .map(([k]) => String(k).toLowerCase());
}

function getProductOccasions(product) {
  const scores = product?.ai_metadata?.occasions;
  if (!scores || typeof scores !== 'object') return [];
  return Object.entries(scores)
    .filter(([_, v]) => Number(v) >= 50)
    .map(([k]) => String(k).toLowerCase());
}

function getProductMaterials(product) {
  const m = product?.ai_metadata?.material || product?.ai_metadata?.materials;
  if (Array.isArray(m)) return m.map(s => String(s).toLowerCase());
  if (typeof m === 'string') return [m.toLowerCase()];
  return [];
}

// ─── computeAvailableFilters ─────────────────────────────────────
// Renvoie la structure des filtres disponibles depuis un tableau de produits.
//
// v1.12.354 — Compteurs DYNAMIQUES : pour chaque dimension de filtre (colors,
// brands, sizes, etc.), on compte les produits qui matchent TOUS les filtres
// ACTIFS SAUF celui de cette dimension. Permet à l'user de voir combien il
// reste s'il ajoute UNE option de cette dimension.
//
// Exemple : si currentFilters = { category:['Robes'], brands:['Pinko'] }
//   - colorCounts utilise les robes Pinko (ignore le filtre brands car on est sur colors)
//   - en fait pour brands aussi : robes (ignore Pinko car on est sur brands)
//
// Cette fonction est PURE et synchrone.
export function computeAvailableFilters(products, opts = {}) {
  const safe = Array.isArray(products) ? products : [];
  const usdToEurRate = opts.usdToEurRate || 0.92;
  const currentFilters = opts.currentFilters || {};
  const total = safe.length;

  // Pré-calcule les pools "products matching all filters EXCEPT one dimension"
  const poolExcept = (excludeKey) => {
    const f = { ...currentFilters };
    delete f[excludeKey];
    // Si pas de filtres restants, retourne tout
    if (Object.keys(f).length === 0) return safe;
    return applyFilters(safe, f, { usdToEurRate });
  };
  // Cache lazy : ne calcule un pool que si on en a besoin
  const pools = {};
  const getPool = (k) => {
    if (!pools[k]) pools[k] = poolExcept(k);
    return pools[k];
  };

  // ─── Catégories ───
  const categoryCounts = {};
  for (const p of getPool('category')) {
    const cat = p?.category;
    if (cat && CATEGORY_IDS.includes(cat)) {
      categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
    }
  }

  // ─── Couleurs ───
  const colorCounts = {};
  for (const p of getPool('colors')) {
    const c = detectColor(p);
    if (c) colorCounts[c] = (colorCounts[c] || 0) + 1;
  }

  // ─── Prix : min/max + tranches ───
  // min/max calculés sur tout (utiles pour slider)
  let priceMin = Infinity, priceMax = 0;
  for (const p of safe) {
    const eur = getProductPriceEur(p, usdToEurRate);
    if (eur > 0) {
      if (eur < priceMin) priceMin = eur;
      if (eur > priceMax) priceMax = eur;
    }
  }
  if (!isFinite(priceMin)) priceMin = 0;
  const pricePool = getPool('price_min'); // applies all filters EXCEPT price
  const priceRangeCounts = PRICE_RANGES.map(r => {
    const count = pricePool.filter(p => {
      const eur = getProductPriceEur(p, usdToEurRate);
      if (eur <= 0) return false;
      if (r.min !== null && eur < r.min) return false;
      if (r.max !== null && eur >= r.max) return false;
      return true;
    }).length;
    return { ...r, count };
  });

  // ─── Marques ───
  const brandCounts = {};
  for (const p of getPool('brands')) {
    const b = (p?.brand || '').trim();
    if (b && b.length >= 2) brandCounts[b] = (brandCounts[b] || 0) + 1;
  }
  const brandsSorted = Object.entries(brandCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([value, count]) => ({ value, label: value, count }));

  // ─── Tailles ───
  const sizeCounts = {};
  for (const p of getPool('sizes')) {
    const sizes = p?.sizes_available || p?.sizes || p?.ai_metadata?.sizes;
    if (Array.isArray(sizes)) {
      for (const s of sizes) {
        const v = String(s).trim();
        if (v) sizeCounts[v] = (sizeCounts[v] || 0) + 1;
      }
    }
  }

  // ─── Tier partenaire ───
  const tierCounts = { tier_1: 0, tier_2: 0, tier_3: 0 };
  for (const p of getPool('partner_tier')) {
    const t = getProductTier(p);
    tierCounts[t] = (tierCounts[t] || 0) + 1;
  }

  // ─── Partenaires (multi-select) ───
  const partnerCounts = {};
  for (const p of getPool('partners')) {
    const k = p?.partner;
    if (k) partnerCounts[k] = (partnerCounts[k] || 0) + 1;
  }

  // ─── Styles (depuis ai_metadata) ───
  const styleCounts = {};
  for (const p of getPool('styles')) {
    for (const s of getProductStyles(p)) {
      styleCounts[s] = (styleCounts[s] || 0) + 1;
    }
  }

  // ─── Matières ───
  const materialCounts = {};
  for (const p of getPool('materials')) {
    for (const m of getProductMaterials(p)) {
      materialCounts[m] = (materialCounts[m] || 0) + 1;
    }
  }

  // ─── Occasions ───
  const occasionCounts = {};
  for (const p of getPool('occasions')) {
    for (const o of getProductOccasions(p)) {
      occasionCounts[o] = (occasionCounts[o] || 0) + 1;
    }
  }

  return {
    total,
    categories: CATEGORY_IDS.map(id => ({ value: id, count: categoryCounts[id] || 0 })),
    colors: COLOR_PALETTE.map(c => ({ value: c.value, hex: c.hex, count: colorCounts[c.value] || 0 })),
    price: {
      min: Math.floor(priceMin),
      max: Math.ceil(priceMax),
      ranges: priceRangeCounts,
    },
    brands: brandsSorted,
    sizes: Object.entries(sizeCounts)
      .sort((a, b) => (parseInt(a[0]) || 0) - (parseInt(b[0]) || 0))
      .map(([value, count]) => ({ value, count })),
    partnerTiers: Object.entries(tierCounts).map(([value, count]) => ({
      value, count, ...TIER_LABELS[value],
    })),
    partners: PARTNER_OPTIONS.map(opt => ({
      ...opt, count: partnerCounts[opt.value] || 0,
    })).filter(o => o.count > 0),
    styles:    STYLE_KEYS.map(k    => ({ value: k, count: styleCounts[k]    || 0 })).filter(o => o.count > 0),
    materials: MATERIAL_KEYS.map(k => ({ value: k, count: materialCounts[k] || 0 })).filter(o => o.count > 0),
    occasions: OCCASION_KEYS.map(k => ({ value: k, count: occasionCounts[k] || 0 })).filter(o => o.count > 0),
  };
}

// ─── applyFilters ───────────────────────────────────────────────
// Applique un ensemble de filtres sur un tableau de produits et renvoie
// les produits qui matchent TOUS les critères (AND entre filtres).
// Pour les filtres multi-valeurs (colors, brands, etc.) c'est OR à l'intérieur.
//
// filters : {
//   category?: string[],
//   subcategory?: string[],   (matchésubfilter dans CatalogScreen)
//   colors?: string[],
//   brands?: string[],
//   price_min?: number,       (EUR)
//   price_max?: number,       (EUR)
//   sizes?: string[],
//   partner_tier?: string[],  ('tier_1'|'tier_2'|'tier_3')
//   styles?: string[],
//   materials?: string[],
//   occasions?: string[],
// }
export function applyFilters(products, filters = {}, opts = {}) {
  if (!Array.isArray(products)) return [];
  const usdToEurRate = opts.usdToEurRate || 0.92;
  const f = filters || {};

  return products.filter(p => {
    if (!p) return false;

    // Catégorie
    if (Array.isArray(f.category) && f.category.length > 0) {
      if (!f.category.includes(p.category)) return false;
    }

    // Couleur
    if (Array.isArray(f.colors) && f.colors.length > 0) {
      const c = detectColor(p);
      if (!c || !f.colors.includes(c)) return false;
    }

    // Marque
    if (Array.isArray(f.brands) && f.brands.length > 0) {
      if (!p.brand || !f.brands.includes(p.brand)) return false;
    }

    // Prix
    if (typeof f.price_min === 'number' || typeof f.price_max === 'number') {
      const eur = getProductPriceEur(p, usdToEurRate);
      if (eur <= 0) return false;
      if (typeof f.price_min === 'number' && eur < f.price_min) return false;
      if (typeof f.price_max === 'number' && eur > f.price_max) return false;
    }

    // Taille
    if (Array.isArray(f.sizes) && f.sizes.length > 0) {
      const sizes = p?.sizes_available || p?.sizes || p?.ai_metadata?.sizes;
      if (!Array.isArray(sizes) || sizes.length === 0) return false;
      const sStr = sizes.map(s => String(s));
      if (!f.sizes.some(s => sStr.includes(s))) return false;
    }

    // Tier partenaire
    if (Array.isArray(f.partner_tier) && f.partner_tier.length > 0) {
      if (!f.partner_tier.includes(getProductTier(p))) return false;
    }

    // Partenaires (multi-select)
    if (Array.isArray(f.partners) && f.partners.length > 0) {
      if (!f.partners.includes(p.partner)) return false;
    }

    // Style (AI metadata)
    if (Array.isArray(f.styles) && f.styles.length > 0) {
      const styles = getProductStyles(p);
      if (!f.styles.some(s => styles.includes(s))) return false;
    }

    // Matière
    if (Array.isArray(f.materials) && f.materials.length > 0) {
      const mats = getProductMaterials(p);
      if (!f.materials.some(m => mats.includes(m))) return false;
    }

    // Occasion
    if (Array.isArray(f.occasions) && f.occasions.length > 0) {
      const occs = getProductOccasions(p);
      if (!f.occasions.some(o => occs.includes(o))) return false;
    }

    return true;
  });
}

// ─── countActiveFilters ─────────────────────────────────────────
// Compte le nombre de critères actifs (pour afficher "Filtres (N)")
export function countActiveFilters(filters = {}) {
  if (!filters) return 0;
  let n = 0;
  if (Array.isArray(filters.category)     && filters.category.length     > 0) n++;
  if (Array.isArray(filters.subcategory)  && filters.subcategory.length  > 0) n++;
  if (Array.isArray(filters.colors)       && filters.colors.length       > 0) n++;
  if (Array.isArray(filters.brands)       && filters.brands.length       > 0) n++;
  if (typeof filters.price_min === 'number' || typeof filters.price_max === 'number') n++;
  if (Array.isArray(filters.sizes)        && filters.sizes.length        > 0) n++;
  if (Array.isArray(filters.partner_tier) && filters.partner_tier.length > 0) n++;
  if (Array.isArray(filters.partners)     && filters.partners.length     > 0) n++;
  if (Array.isArray(filters.styles)       && filters.styles.length       > 0) n++;
  if (Array.isArray(filters.materials)    && filters.materials.length    > 0) n++;
  if (Array.isArray(filters.occasions)    && filters.occasions.length    > 0) n++;
  return n;
}

// ─── buildActiveChips ───────────────────────────────────────────
// Construit la liste des chips actifs (label + filter_id + value) pour
// affichage horizontal scrollable avec bouton ✕ removable.
//
// Bilingue : passe `locale` ('fr'|'en') pour traduire les labels (catégorie,
// couleur, style, matière, occasion, partenaire, prix, taille).
const CHIP_LABELS = {
  fr: {
    cat: { 'Robes':'Robes','Hauts':'Hauts','Bas':'Bas','Vestes':'Vestes','Manteaux':'Manteaux','Chaussures':'Chaussures','Sacs':'Sacs','Bijoux':'Bijoux','Accessoires':'Accessoires','Maillots':'Maillots' },
    color: { noir:'Noir',blanc:'Blanc',gris:'Gris',beige:'Beige',marron:'Marron',marine:'Marine',bleu:'Bleu',rouge:'Rouge',rose:'Rose',vert:'Vert',jaune:'Jaune',orange:'Orange',violet:'Violet','doré':'Doré','argenté':'Argenté',multicolore:'Multicolore' },
    style: { chic:'Chic',casual:'Casual',smart_casual:'Smart Casual',sportswear:'Sport',bohemian:'Bohème',romantic:'Romantique',edgy:'Edgy',vintage:'Vintage',minimalist:'Minimaliste',glamour:'Glamour' },
    material: { coton:'Coton',soie:'Soie',cuir:'Cuir',laine:'Laine',cachemire:'Cachemire',denim:'Denim',polyester:'Polyester',lin:'Lin',velours:'Velours',satin:'Satin' },
    occasion: { travail:'Travail',brunch:'Brunch','soirée':'Soirée',sport:'Sport','cérémonie':'Cérémonie',casual:'Casual',voyage:'Voyage',date:'Rendez-vous' },
    sizePrefix: 'T.',
    priceFmt: (lo, hi) => hi !== null ? `${lo}-${hi}€` : `≥ ${lo}€`,
    tierKey: 'fr',
  },
  en: {
    cat: { 'Robes':'Dresses','Hauts':'Tops','Bas':'Bottoms','Vestes':'Jackets','Manteaux':'Coats','Chaussures':'Shoes','Sacs':'Bags','Bijoux':'Jewelry','Accessoires':'Accessories','Maillots':'Swimwear' },
    color: { noir:'Black',blanc:'White',gris:'Gray',beige:'Beige',marron:'Brown',marine:'Navy',bleu:'Blue',rouge:'Red',rose:'Pink',vert:'Green',jaune:'Yellow',orange:'Orange',violet:'Purple','doré':'Gold','argenté':'Silver',multicolore:'Multicolor' },
    style: { chic:'Chic',casual:'Casual',smart_casual:'Smart Casual',sportswear:'Sportswear',bohemian:'Bohemian',romantic:'Romantic',edgy:'Edgy',vintage:'Vintage',minimalist:'Minimalist',glamour:'Glamour' },
    material: { coton:'Cotton',soie:'Silk',cuir:'Leather',laine:'Wool',cachemire:'Cashmere',denim:'Denim',polyester:'Polyester',lin:'Linen',velours:'Velvet',satin:'Satin' },
    occasion: { travail:'Work',brunch:'Brunch','soirée':'Evening',sport:'Sport','cérémonie':'Ceremony',casual:'Casual',voyage:'Travel',date:'Date' },
    sizePrefix: 'Size',
    priceFmt: (lo, hi) => hi !== null ? `€${lo}-${hi}` : `≥ €${lo}`,
    tierKey: 'en',
  },
};

export function buildActiveChips(filters = {}, locale = 'fr') {
  const chips = [];
  const f = filters || {};
  const isFr = String(locale || '').startsWith('fr');
  const L = isFr ? CHIP_LABELS.fr : CHIP_LABELS.en;

  for (const cat of (f.category || []))    chips.push({ filter_id: 'category',     value: cat, label: L.cat[cat]     || cat });
  for (const sub of (f.subcategory || [])) chips.push({ filter_id: 'subcategory',  value: sub, label: sub });
  for (const col of (f.colors || []))      chips.push({ filter_id: 'colors',       value: col, label: L.color[col]   || col });
  for (const br of (f.brands || []))       chips.push({ filter_id: 'brands',       value: br,  label: br });
  if (typeof f.price_min === 'number' || typeof f.price_max === 'number') {
    const lo = typeof f.price_min === 'number' ? f.price_min : 0;
    const hi = typeof f.price_max === 'number' ? f.price_max : null;
    chips.push({ filter_id: 'price', value: `${lo}-${hi ?? ''}`, label: L.priceFmt(lo, hi) });
  }
  for (const sz of (f.sizes || []))        chips.push({ filter_id: 'sizes',        value: sz,  label: `${L.sizePrefix} ${sz}` });
  for (const tr of (f.partner_tier || [])) chips.push({ filter_id: 'partner_tier', value: tr,  label: TIER_LABELS[tr]?.[L.tierKey] || tr });
  for (const pa of (f.partners || []))     chips.push({ filter_id: 'partners',     value: pa,  label: (PARTNER_OPTIONS.find(o => o.value === pa)?.label) || pa });
  for (const st of (f.styles || []))       chips.push({ filter_id: 'styles',       value: st,  label: L.style[st]    || st });
  for (const m of (f.materials || []))     chips.push({ filter_id: 'materials',    value: m,   label: L.material[m]  || m });
  for (const o of (f.occasions || []))     chips.push({ filter_id: 'occasions',    value: o,   label: L.occasion[o]  || o });

  return chips;
}

// ─── removeFilter ───────────────────────────────────────────────
// Retire une valeur d'un filtre actif (ou tout le filtre si valeur absente).
export function removeFilter(filters, filterId, value) {
  const next = { ...filters };
  if (filterId === 'price') {
    delete next.price_min;
    delete next.price_max;
    return next;
  }
  if (Array.isArray(next[filterId])) {
    next[filterId] = next[filterId].filter(v => v !== value);
    if (next[filterId].length === 0) delete next[filterId];
  } else {
    delete next[filterId];
  }
  return next;
}

// ─── clearAllFilters ────────────────────────────────────────────
export function clearAllFilters() {
  return {};
}

// ─── Préférences user (AsyncStorage) ─────────────────────────────
export async function loadPreferences() {
  try {
    const raw = await AsyncStorage.getItem(PREF_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    console.warn('[FiltersService] loadPreferences failed:', e?.message);
    return null;
  }
}

export async function savePreferences(prefs) {
  try {
    const clean = {
      preferred_size_system: prefs?.preferred_size_system || 'FR',
      preferred_sizes:       prefs?.preferred_sizes       || {},
      favorite_brands:       Array.isArray(prefs?.favorite_brands)   ? prefs.favorite_brands.slice(0, 20)   : [],
      favorite_styles:       Array.isArray(prefs?.favorite_styles)   ? prefs.favorite_styles.slice(0, 10)   : [],
      preferred_colors:      Array.isArray(prefs?.preferred_colors)  ? prefs.preferred_colors.slice(0, 10)  : [],
      price_range_preferred: Array.isArray(prefs?.price_range_preferred) ? prefs.price_range_preferred.slice(0, 2) : null,
      saved_at: Date.now(),
    };
    await AsyncStorage.setItem(PREF_STORAGE_KEY, JSON.stringify(clean));
    return clean;
  } catch (e) {
    console.warn('[FiltersService] savePreferences failed:', e?.message);
    return null;
  }
}

export async function clearPreferences() {
  try { await AsyncStorage.removeItem(PREF_STORAGE_KEY); } catch {}
}

// ─── prefsToFilters ─────────────────────────────────────────────
// Convertit des préférences sauvegardées en filtres initiaux à appliquer.
export function prefsToFilters(prefs) {
  if (!prefs) return {};
  const f = {};
  if (Array.isArray(prefs.preferred_colors) && prefs.preferred_colors.length > 0) {
    f.colors = prefs.preferred_colors.slice();
  }
  if (Array.isArray(prefs.favorite_brands) && prefs.favorite_brands.length > 0) {
    f.brands = prefs.favorite_brands.slice();
  }
  if (Array.isArray(prefs.favorite_styles) && prefs.favorite_styles.length > 0) {
    f.styles = prefs.favorite_styles.slice();
  }
  if (Array.isArray(prefs.price_range_preferred) && prefs.price_range_preferred.length === 2) {
    f.price_min = Number(prefs.price_range_preferred[0]) || 0;
    f.price_max = Number(prefs.price_range_preferred[1]) || null;
  }
  return f;
}

export default {
  computeAvailableFilters,
  applyFilters,
  countActiveFilters,
  buildActiveChips,
  removeFilter,
  clearAllFilters,
  loadPreferences,
  savePreferences,
  clearPreferences,
  prefsToFilters,
  COLOR_PALETTE,
  CATEGORY_IDS,
  PRICE_RANGES,
  PARTNER_TIERS,
  TIER_LABELS,
  STYLE_KEYS,
  MATERIAL_KEYS,
  OCCASION_KEYS,
};
